/**
 * Test Run Modal
 * Prompts user for test email and phone, then runs automation test
 */
import React, { useState } from 'react';
import { X, FlaskConical, Loader2, CheckCircle, XCircle, Clock, Mail, Phone } from 'lucide-react';

const TestRunModal = ({ isOpen, onClose, onRunTest, automationName }) => {
    const [testEmail, setTestEmail] = useState('');
    const [testPhone, setTestPhone] = useState('');
    const [isRunning, setIsRunning] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [error, setError] = useState(null);

    if (!isOpen) return null;

    const handleRunTest = async () => {
        if (!testEmail && !testPhone) {
            setError('Please provide at least an email or phone number');
            return;
        }

        setIsRunning(true);
        setError(null);
        setTestResult(null);

        try {
            const result = await onRunTest({ testEmail, testPhone });
            setTestResult(result);
        } catch (err) {
            setError(err.message || 'Test run failed');
        } finally {
            setIsRunning(false);
        }
    };

    const handleClose = () => {
        setTestEmail('');
        setTestPhone('');
        setTestResult(null);
        setError(null);
        onClose();
    };

    const getStepStatusIcon = (status) => {
        switch (status) {
            case 'sent':
                return <CheckCircle className="w-4 h-4 text-emerald-500" />;
            case 'skipped':
                return <Clock className="w-4 h-4 text-orange-500" />;
            case 'failed':
                return <XCircle className="w-4 h-4 text-red-500" />;
            default:
                return <Clock className="w-4 h-4 text-stone-400" />;
        }
    };

    const getStepTypeIcon = (type) => {
        switch (type) {
            case 'email':
                return <Mail className="w-4 h-4 text-stone-500" />;
            case 'sms':
                return <Phone className="w-4 h-4 text-stone-500" />;
            case 'wait':
                return <Clock className="w-4 h-4 text-stone-500" />;
            default:
                return null;
        }
    };

    const getStepFailureReason = (step) => {
        if (!step || step.status !== 'failed') return null;
        const base = step.error || step.result?.error || 'Step failed';
        const moreInfo = step.result?.response?.moreInfo;
        return moreInfo ? `${base} — ${moreInfo}` : base;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-stone-200">
                    <div className="flex items-center gap-2">
                        <FlaskConical className="w-5 h-5 text-stone-600" />
                        <h2 className="text-lg font-semibold text-stone-800">Test Run</h2>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-1 hover:bg-stone-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-stone-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4 overflow-y-auto flex-1">
                    {!testResult ? (
                        <>
                            <p className="text-sm text-stone-600 mb-4">
                                Run a test of <strong>{automationName || 'this automation'}</strong> using sample contact data.
                                All messages will be prefixed with <strong>[TEST]</strong>.
                            </p>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1">
                                        Test Email
                                    </label>
                                    <input
                                        type="email"
                                        value={testEmail}
                                        onChange={(e) => setTestEmail(e.target.value)}
                                        placeholder="test@example.com"
                                        className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        disabled={isRunning}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1">
                                        Test Phone
                                    </label>
                                    <input
                                        type="tel"
                                        value={testPhone}
                                        onChange={(e) => setTestPhone(e.target.value)}
                                        placeholder="+1 555 123 4567"
                                        className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        disabled={isRunning}
                                    />
                                </div>

                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                    <p className="text-xs text-amber-800">
                                        <strong>Note:</strong> Test runs execute immediately, skip wait steps, and ignore guardrails.
                                        No execution records are created.
                                    </p>
                                </div>
                            </div>

                            {error && (
                                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                                    <p className="text-sm text-red-700">{error}</p>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            {/* Test Results */}
                            <div className="mb-4">
                                <div className={`flex items-center gap-2 p-3 rounded-lg ${testResult.completed
                                        ? 'bg-emerald-50 border border-emerald-200'
                                        : 'bg-red-50 border border-red-200'
                                    }`}>
                                    {testResult.completed ? (
                                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                                    ) : (
                                        <XCircle className="w-5 h-5 text-red-500" />
                                    )}
                                    <span className={`text-sm font-medium ${testResult.completed ? 'text-emerald-700' : 'text-red-700'
                                        }`}>
                                        {testResult.completed ? 'Test completed successfully!' : 'Test completed with errors'}
                                    </span>
                                </div>
                            </div>

                            {/* Summary */}
                            <div className="grid grid-cols-3 gap-2 mb-4">
                                <div className="bg-emerald-50 rounded-lg p-2 text-center">
                                    <div className="text-lg font-bold text-emerald-700">{testResult.summary?.sent || 0}</div>
                                    <div className="text-xs text-emerald-600">Sent</div>
                                </div>
                                <div className="bg-orange-50 rounded-lg p-2 text-center">
                                    <div className="text-lg font-bold text-orange-700">{testResult.summary?.skipped || 0}</div>
                                    <div className="text-xs text-orange-600">Skipped</div>
                                </div>
                                <div className="bg-red-50 rounded-lg p-2 text-center">
                                    <div className="text-lg font-bold text-red-700">{testResult.summary?.failed || 0}</div>
                                    <div className="text-xs text-red-600">Failed</div>
                                </div>
                            </div>

                            {/* Step Details */}
                            <div className="space-y-2">
                                <h3 className="text-sm font-medium text-stone-700">Step Results</h3>
                                {testResult.steps?.map((step, index) => (
                                    <div
                                        key={step.stepId || index}
                                        className="p-2 bg-stone-50 rounded-lg"
                                    >
                                        <div className="flex items-center gap-3">
                                            {getStepTypeIcon(step.type)}
                                            <span className="flex-1 text-sm text-stone-700 capitalize">{step.type}</span>
                                            <div className="flex items-center gap-1">
                                                {getStepStatusIcon(step.status)}
                                                <span className={`text-xs ${step.status === 'sent' ? 'text-emerald-600' :
                                                        step.status === 'skipped' ? 'text-orange-600' :
                                                            step.status === 'failed' ? 'text-red-600' : 'text-stone-500'
                                                    }`}>
                                                    {step.status}
                                                </span>
                                            </div>
                                        </div>

                                        {getStepFailureReason(step) && (
                                            <p className="mt-1 text-xs text-red-600 pl-7">{getStepFailureReason(step)}</p>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Error details */}
                            {testResult.steps?.some(s => s.error || s.result?.error) && (
                                <div className="mt-4 space-y-2">
                                    <h3 className="text-sm font-medium text-red-700">Errors</h3>
                                    {testResult.steps.filter(s => s.error || s.result?.error).map((step, index) => (
                                        <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-2">
                                            <p className="text-xs text-red-700">
                                                <strong className="capitalize">{step.type}:</strong> {step.error || step.result?.error}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 p-4 border-t border-stone-200 bg-stone-50">
                    {!testResult ? (
                        <>
                            <button
                                onClick={handleClose}
                                disabled={isRunning}
                                className="px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRunTest}
                                disabled={isRunning || (!testEmail && !testPhone)}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isRunning || (!testEmail && !testPhone)
                                        ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                    }`}
                            >
                                {isRunning ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>Running...</span>
                                    </>
                                ) : (
                                    <>
                                        <FlaskConical className="w-4 h-4" />
                                        <span>Run Test</span>
                                    </>
                                )}
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 text-sm font-medium bg-stone-700 text-white hover:bg-stone-800 rounded-lg transition-colors"
                        >
                            Done
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TestRunModal;
